import React from 'react';
import { Info } from 'lucide-react';
import type { IframeEmbedData } from './types';

interface IframeTabProps {
  data: IframeEmbedData;
  onChange: (field: string, value: any) => void;
}

export const IframeTab: React.FC<IframeTabProps> = ({ data, onChange }) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Generic Iframe</p>
            <p>
              Embed any content using a direct URL. Good for interactive content, widgets, or custom
              applications.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Source URL *</label>
        <input
          type="url"
          value={data.src}
          onChange={e => onChange('src', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="https://example.com/embed"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={data.title}
          onChange={e => onChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Embedded content"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Width</label>
          <input
            type="text"
            value={data.width}
            onChange={e => onChange('width', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="100% or 800px"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
          <input
            type="text"
            value={data.height}
            onChange={e => onChange('height', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="400 or 600px"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Allow Permissions</label>
        <input
          type="text"
          value={data.allow}
          onChange={e => onChange('allow', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
          placeholder="accelerometer; autoplay; clipboard-write"
        />
        <p className="mt-1 text-xs text-gray-500">Semicolon-separated list of iframe permissions</p>
      </div>

      <div className="space-y-2">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={data.allowFullscreen}
            onChange={e => onChange('allowFullscreen', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Allow Fullscreen</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={!data.frameBorder}
            onChange={e => onChange('frameBorder', !e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Hide Border</span>
        </label>
      </div>
    </div>
  );
};
